import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export type EmailProtocol = "IMAP" | "POP3" | "MICROSOFT_OAUTH";

export interface InboundEmailConfig {
  id: string;
  emailAddress: string;
  /** Optional for MICROSOFT_OAUTH; omitted in API responses unless applicable. */
  password?: string | null;
  host: string;
  port: number;
  protocol: EmailProtocol;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
  useSsl: boolean;
  enabled: boolean;
  pollingIntervalSeconds: number;
  folderName: string;
  lastPollTime?: string;
  lastPollError?: string;
  isDeleted: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdDate: string;
  timestamp?: string;
  version: number;
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  unreadEmailCount?: number;
}

export interface InboundEmailConfigParams extends Params<InboundEmailConfig> {
  enabled?: boolean;
}

// Fetch all configs (paginated)
export async function fetchInboundEmailConfigs(
  params: InboundEmailConfigParams,
): Promise<IPageable<InboundEmailConfig>> {
  try {
    const response = await axiosInstance.get("/inbound-email-config", {
      params,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching inbound email configurations.",
    );
  }
}

// Get the currently active config
export async function fetchActiveInboundEmailConfig(): Promise<InboundEmailConfig | null> {
  try {
    const response = await axiosInstance.get("/inbound-email-config/active");
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the active inbound email configuration.",
    );
  }
}

// Get config by ID
export async function fetchInboundEmailConfigById(
  id: string,
): Promise<InboundEmailConfig> {
  try {
    const response = await axiosInstance.get(`/inbound-email-config/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the inbound email configuration.",
    );
  }
}

// Create new config
export async function createInboundEmailConfig(
  data: Partial<InboundEmailConfig>,
): Promise<InboundEmailConfig> {
  try {
    const response = await axiosInstance.post("/inbound-email-config", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while creating the inbound email configuration.",
    );
  }
}

// Update config
export async function updateInboundEmailConfig(
  id: string,
  data: Partial<InboundEmailConfig>,
): Promise<InboundEmailConfig> {
  try {
    const response = await axiosInstance.put(
      `/inbound-email-config/${id}`,
      data,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the inbound email configuration.",
    );
  }
}

// Soft delete config
export async function deleteInboundEmailConfig(id: string): Promise<void> {
  try {
    await axiosInstance.delete(`/inbound-email-config/${id}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the inbound email configuration.",
    );
  }
}

// Test email connection
export async function testInboundEmailConnection(
  id: string,
): Promise<ConnectionTestResult> {
  try {
    const response = await axiosInstance.post(
      `/inbound-email-config/${id}/test-connection`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while testing the email connection.",
    );
  }
}

// Manually trigger email polling
export async function pollInboundEmailNow(id: string): Promise<void> {
  try {
    await axiosInstance.post(`/inbound-email-config/${id}/poll-now`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while triggering email polling.",
    );
  }
}

// React Query hook for fetching configs
export const useInboundEmailConfigsQuery = (
  params: InboundEmailConfigParams,
): UseQueryResult<IPageable<InboundEmailConfig>, Error> => {
  const queryKey = ["inboundEmailConfig", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchInboundEmailConfigs(params),
  });
};

// React Query hook for fetching active config
export const useActiveInboundEmailConfigQuery = (): UseQueryResult<
  InboundEmailConfig | null,
  Error
> => {
  return useQuery({
    queryKey: ["inboundEmailConfig", "active"],
    queryFn: fetchActiveInboundEmailConfig,
  });
};

// Protocol options for dropdown
export const PROTOCOL_OPTIONS = [
  { label: "IMAP", value: "IMAP" },
  { label: "POP3", value: "POP3" },
  { label: "Microsoft (OAuth)", value: "MICROSOFT_OAUTH" },
];
